import React, { useState } from 'react';
import { Typography, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';

import Table from '../../../../components/tables/Table';
import tableStyles from '../../../../components/tables/Table.styles';
import MeterActionsDialog from '../Edit/MeterActionsDialog';
import { getMeterTypeName, coerceFaulty } from '../../utils/meter.helpers';
import { useNavWithTrail } from '../../../../layouts/TrailProvider';

const COLUMNS = [
  {
    label: 'Meter Number',
    field: 'meterNumber',
    type: 'string',
    sortable: true,
    render: (row, v, nav) => (
      <Typography
        sx={tableStyles.tableLink}
        onClick={() =>
          nav(
            `/home/meters/${encodeURIComponent(v)}`,
            {
              state: {
                unit: row?.unit,
                evn: row?.evn,
              },
            }
          )
        }
      >
        {v}
      </Typography>
    ),
  },
  {
    label: 'From Date',
    field: 'fromDate.iso',
    type: 'date',
    sortable: true,
    accessor: (row) => row?.fromDate?.iso ?? '',
    render: (row) =>
      row?.fromDate?.display ??
      (row?.fromDate?.iso
        ? new Date(row.fromDate.iso).toLocaleDateString('en-GB')
        : '—'),
  },
  {
    label: 'Unit',
    field: 'unit',
    type: 'string',
    sortable: true,
    render: (row, v, nav) => (
      <Typography
        sx={tableStyles.tableLink}
        onClick={() => nav(`/home/units/${encodeURIComponent(v)}`)}
      >
        {v}
      </Typography>
    ),
  },
  {
    label: 'EVN',
    field: 'evn',
    type: 'string',
    sortable: true,
    render: (row, v, nav) => (
      <Typography
        sx={tableStyles.tableLink}
        onClick={() => nav(`/home/evns/${encodeURIComponent(v)}`)}
      >
        {v}
      </Typography>
    ),
  },
  {
    label: 'Meter Type',
    field: 'meterType',
    type: 'string',
    sortable: true,
    accessor: (row) => getMeterTypeName(row?.meterType),
  },
  {
    label: 'Faulty',
    field: '__faulty',
    type: 'boolean',
    sortable: true,
    accessor: (row) =>
      coerceFaulty(row?.faulty ?? row?.Faulty ?? row?.IsFaulty),
  },
];

export default function MeterTable({ rows = [], actionsEnabled }) {
  const navigate = useNavigate();
  const nav = useNavWithTrail(navigate);

  const [selectedRow, setSelectedRow] = useState(null);
  const [actionsOpen, setActionsOpen] = useState(false);

  const actionsRenderer = actionsEnabled
    ? (row) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <IconButton
            size="small"
            sx={tableStyles.compactActionButton}
            onClick={() => {
              setSelectedRow({
                meterNumber: row?.meterNumber,
                fromDate: row?.fromDate?.iso || row?.fromDate || '',
                faulty: row?.faulty ?? row?.Faulty ?? row?.IsFaulty,
              });
              setActionsOpen(true);
            }}
            aria-label={`Edit ${row?.meterNumber}`}
          >
            <EditIcon sx={tableStyles.editIcon} />
          </IconButton>
        </div>
      )
    : null;

  const columnsWithNav = COLUMNS.map((c) =>
    c.render
      ? { ...c, render: (row, value) => c.render(row, value ?? row?.[c.field], nav) }
      : c
  );

  return (
    <>
      <Table
        rows={Array.isArray(rows) ? rows : []}
        columns={columnsWithNav}
        actionsRenderer={actionsRenderer}
        actionsHeader={actionsEnabled ? 'Actions' : null}
        stickyHeader
        zebra
        hover
        bordered

      />

      {actionsOpen && selectedRow && (
        <MeterActionsDialog
          open
          onClose={() => {
            setActionsOpen(false);
            setSelectedRow(null);
          }}
          initialData={selectedRow}
          onSubmit={() => {
            setActionsOpen(false);
            setSelectedRow(null);
          }}
          defaultTabKey="modify"
        />
      )}
    </>
  );
}
